// Considered: MeasureMembersListMolecule — rejected because it is the parent-side roster with unlink; add mode links one
// edge from the member side. See spec Reuse Audit.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import { formatWeight } from '../RelatedSpheresEditor/formatWeight';
import DrawerAtom from '../../atoms/Drawer/DrawerAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import ChoiceCardGroupAtom from '../../atoms/ChoiceCardGroup/ChoiceCardGroupAtom';
import ConfirmDialogAtom from '../../atoms/ConfirmDialog/ConfirmDialogAtom';
import InputNumberAtom from '../../atoms/InputNumber/InputNumberAtom';
import MessageAtom from '../../atoms/Message/MessageAtom';
import SkeletonAtom from '../../atoms/Skeleton/SkeletonAtom';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useSpherePermission } from '../../../hooks/useSpherePermission';
import MeasurePicker, { type PickableMeasure } from './MeasurePicker';
import { useMeasureFactors } from './useMeasureFactors';
import { measureFactorChoices } from './measureFactorChoices';
import './MeasureSheetMolecule.scss';

export interface LinkedMeasure {
  parentId: string;
  factorId: string;
  measureName: string;
}

export interface MeasureAddSheetProps {
  visible: boolean;
  onHide: () => void;
  /** The sphere that would roll up: the member side of the new edge. */
  memberId: string;
  /** Destinations the card already lists. A non-excluded one opens view mode; an excluded one can be added again. */
  existing: Array<{ sphereId: string; excluded: boolean }>;
  onViewExisting: (sphereId: string) => void;
  /** Called once the POST answered 2xx, so the card can show its undo strip and reload. */
  onLinked: (linked: LinkedMeasure) => void;
}

type LinkError = 'cycle' | 'forbidden' | 'conflict' | 'other';

const DEFAULT_WEIGHT = 1;

const describeFailure = (err: unknown): { kind: LinkError; reason: string } => {
  const e = err as { response?: { status?: number; data?: { error?: string; message?: string } }; status?: number; message?: string };
  const status = e?.response?.status ?? e?.status;
  const reason = e?.response?.data?.error ?? e?.response?.data?.message ?? e?.message ?? '';
  if (status === 400 && /cycle/i.test(reason)) return { kind: 'cycle', reason };
  if (status === 403) return { kind: 'forbidden', reason };
  if (status === 409) return { kind: 'conflict', reason };
  return { kind: 'other', reason };
};

const MeasureAddSheet: React.FC<MeasureAddSheetProps> = ({ visible, onHide, memberId, existing, onViewExisting, onLinked }) => {
  const { t, i18n } = useTranslation();
  const graphClient = useApiClientsOrNull()?.graphClient;
  const logAnalyticsEvent = useAnalytics();

  const [picked, setPicked] = useState<PickableMeasure | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [weight, setWeight] = useState<number | null>(DEFAULT_WEIGHT);
  const [touched, setTouched] = useState(false);
  const [checkpoint, setCheckpoint] = useState(false);
  const [posting, setPosting] = useState(false);
  const [failure, setFailure] = useState<{ kind: LinkError; reason: string } | null>(null);

  const {
    factors,
    loading: factorsLoading,
    error: factorsError,
    forbidden: cannotView,
    reload: reloadFactors,
  } = useMeasureFactors(picked?.id ?? null);
  const permission = useSpherePermission(picked?.id, 'edit');

  useEffect(() => {
    if (factors.length === 1) setFactorId(factors[0].id);
    else setFactorId((current) => (factors.some((factor) => factor.id === current) ? current : null));
  }, [factors]);

  const measureName = picked?.name ?? '';
  const liftsExclusion = existing.some((row) => row.sphereId === picked?.id && row.excluded);
  const invalid = weight === null || weight < 0;
  const showInvalid = invalid && touched;
  const alreadyIn = existing.filter((row) => !row.excluded).map((row) => row.sphereId);

  const reset = () => {
    setPicked(null);
    setFactorId(null);
    setWeight(DEFAULT_WEIGHT);
    setTouched(false);
    setCheckpoint(false);
    setFailure(null);
  };

  const close = () => {
    reset();
    onHide();
  };

  const pick = (measure: PickableMeasure) => {
    if (alreadyIn.includes(measure.id)) {
      reset();
      onViewExisting(measure.id);
      return;
    }
    setFailure(null);
    setPicked(measure);
  };

  const link = async () => {
    setCheckpoint(false);
    if (!graphClient || !picked || !factorId || weight === null) return;
    setPosting(true);
    setFailure(null);
    try {
      await graphClient.post(`/spheres/${picked.id}/factors/${factorId}/members/${memberId}`, { influence: weight });
    } catch (err) {
      setFailure(describeFailure(err));
      setPosting(false);
      return;
    }
    logAnalyticsEvent('rollup_upward_linked', undefined, {
      weight,
      weightChanged: weight !== DEFAULT_WEIGHT,
      liftedExclusion: liftsExclusion,
    });
    const linked = { parentId: picked.id, factorId, measureName: picked.name };
    setPosting(false);
    reset();
    onHide();
    onLinked(linked);
  };

  /** Why the primary is disabled, or null when it is not. Loading states disable without a reason: they resolve on their own. */
  const blockedReason = (): React.ReactNode => {
    if (!picked) return null;
    if (permission.unavailable) {
      return (
        <>
          <p>{t('statusFactors.upward.addFlow.accessUnavailable', { measure: measureName })}</p>
          <ButtonAtom variant="text" size="small" label={t('statusFactors.upward.retry')} onClick={permission.retry} />
        </>
      );
    }
    if (cannotView) {
      return <p>{t('statusFactors.upward.addFlow.noView', { measure: measureName })}</p>;
    }
    if (!permission.loading && !permission.allowed) {
      return <p>{t('statusFactors.upward.addFlow.noEdit', { measure: measureName })}</p>;
    }
    if (!factorsLoading && !factorsError && factors.length === 0) {
      return <p>{t('statusFactors.upward.addFlow.noFactor', { measure: measureName })}</p>;
    }
    if (!factorsLoading && !factorsError && factors.length > 1 && !factorId) {
      return <p>{t('statusFactors.upward.addFlow.chooseFactor', { measure: measureName })}</p>;
    }
    return null;
  };

  const pending = permission.loading || factorsLoading;
  const canRollUp = Boolean(picked) && permission.allowed && !pending && Boolean(factorId) && !invalid && !posting;
  const reason = blockedReason();

  const checkpointMessage = [
    t(
      'statusFactors.upward.addFlow.checkpoint.effect',
      { measure: measureName, weight: formatWeight(weight, i18n.language) }
    ),
    liftsExclusion ? t('statusFactors.upward.addFlow.checkpoint.lifts') : '',
    t('statusFactors.upward.addFlow.checkpoint.undo', { measure: measureName }),
  ]
    .filter(Boolean)
    .join(' ');

  const failureText = (): string => {
    if (!failure) return '';
    const params = { measure: measureName, reason: failure.reason || t('statusFactors.upward.sheet.unknownReason') };
    switch (failure.kind) {
      case 'cycle':
        return t(
          'statusFactors.upward.addFlow.error.cycle',
          params
        );
      case 'forbidden':
        return t('statusFactors.upward.addFlow.error.forbidden', params);
      case 'conflict':
        return t('statusFactors.upward.addFlow.error.conflict', params);
      default:
        return t('statusFactors.upward.addFlow.error.other', params);
    }
  };

  const weightInputId = `measure-add-weight-${memberId}`;
  const reasonId = `${weightInputId}-reason`;

  const renderPicked = () => (
    <>
      <section className="measure-sheet__measure" aria-label={measureName}>
        <div className="measure-sheet__measure-line">
          <span className="measure-sheet__name">{measureName}</span>
          <ButtonAtom
            variant="text"
            size="small"
            label={t('statusFactors.upward.addFlow.change')}
            onClick={() => {
              setPicked(null);
              setFailure(null);
            }}
          />
        </div>
        {factorsLoading && (
          <div className="measure-sheet__loading" aria-busy="true">
            <SkeletonAtom height="var(--font-size-md)" count={2} />
          </div>
        )}
        {factorsError && (
          <div className="measure-sheet__error">
            <MessageAtom
              severity="error"
              text={t('statusFactors.upward.sheet.loadError', { measure: measureName })}
            />
            <ButtonAtom variant="text" size="small" label={t('statusFactors.upward.retry')} onClick={reloadFactors} />
          </div>
        )}
        {factors.length > 1 && (
          <ChoiceCardGroupAtom
            ariaLabel={t('statusFactors.upward.addFlow.factorLabel', { measure: measureName })}
            value={factorId}
            onChange={setFactorId}
            options={measureFactorChoices(factors, t, i18n.language)}
          />
        )}
      </section>
      <section className="measure-sheet__weight">
        <label className="measure-sheet__label" htmlFor={weightInputId}>
          {t('statusFactors.upward.sheet.weightLabel')}
        </label>
        <div onBlur={() => setTouched(true)}>
          <InputNumberAtom
            inputId={weightInputId}
            value={weight}
            onChange={setWeight}
            min={0}
            precision={2}
            step={0.1}
            invalid={showInvalid}
            ariaDescribedBy={reasonId}
          />
        </div>
        <div id={reasonId} className="measure-sheet__reason">
          {showInvalid && (
            <p className="measure-sheet__field-error" role="alert">
              {t('statusFactors.upward.sheet.invalidWeight')}
            </p>
          )}
          {reason}
        </div>
        {failure && <MessageAtom severity="error" text={failureText()} />}
        <ButtonAtom
          className="measure-sheet__primary"
          label={t('statusFactors.upward.addFlow.primary', { measure: measureName })}
          onClick={() => setCheckpoint(true)}
          disabled={!canRollUp}
          loading={posting}
        />
      </section>
    </>
  );

  return (
    <>
      <DrawerAtom
        visible={visible}
        onHide={close}
        position="right"
        width="28rem"
        header={<span className="measure-sheet__title">{t('statusFactors.upward.addFlow.title')}</span>}
        className="measure-sheet"
      >
        <div className="measure-sheet__body">
          {picked ? (
            renderPicked()
          ) : (
            <MeasurePicker memberId={memberId} alreadyRollsUpInto={alreadyIn} onPick={pick} onCancel={close} />
          )}
        </div>
      </DrawerAtom>
      <ConfirmDialogAtom
        visible={checkpoint}
        onHide={() => setCheckpoint(false)}
        header={t('statusFactors.upward.addFlow.checkpoint.header', { measure: measureName })}
        message={checkpointMessage}
        acceptLabel={t('statusFactors.upward.addFlow.primary', { measure: measureName })}
        rejectLabel={t('statusFactors.upward.addFlow.checkpoint.cancel')}
        accept={link}
        variant="warning"
      />
    </>
  );
};

export default MeasureAddSheet;
